import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, ChevronUp, Mail, Image, Video, FileText, Music, Edit, Trash, Plus, X, Search, Check, Share2, Play } from 'lucide-react';
import { DashboardApi } from '../../../apis/DashboardApi';
import { ToastContainer, toast } from 'react-toastify';
import Modal from "react-responsive-modal";
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { AxiosResponse } from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import GroupSharingModal from './GroupSharingModal';

interface GroupMedia {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
}

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
    is_legacy: number;
    created_at?: string;
    file_size?: string;
    recipients?: any;
    recipientTemporaryUrl?: any;
    handleDelete?: () => void;
    handleRename?: () => void;
    handleShare?: () => void;
}

interface GroupState {
    owned: Group[];
    shared: Group[];
}

interface GroupMember {
    id: number;
    email: string;
    user_id?: number;
}

interface Group {
    id: number;
    name: string;
    files: {
        media: GroupMedia;
        added_by?: {  // Add this to track who added each file
            id: number;
            name: string;
        };
    }[];
    members: GroupMember[];
    created_by: {  // Add this to identify group owner
        id: number;
        email: string;
    };
    is_admin: boolean;  // Add this flag from backend
}

interface MediaFile {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
    created_at: string;
    added_by: {
        name: string;
        id: number;
    };
}

interface EmptyStateProps {
    onCreateGroup?: () => void;
}

export const EmptyGroups: React.FC<EmptyStateProps> = ({ onCreateGroup }) => {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed rounded-lg bg-white">
            <div className="bg-gray-50 p-4 rounded-full mb-4">
                <Users className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Groups Yet</h3>
            <p className="text-gray-500 text-center mb-6 max-w-sm">
                Create a group to start sharing media with multiple people at once. Groups make it easy to organize and share your content.
            </p>
            <button
                onClick={onCreateGroup}
                className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
                <Plus className="w-5 h-5" />
                Create Your First Group
            </button>
        </div>
    );
};

export const EmptySharedGroups: React.FC = () => {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed rounded-lg bg-white">
            <div className="bg-gray-50 p-4 rounded-full mb-4">
                <Share2 className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Shared Groups</h3>
            <p className="text-gray-500 text-center max-w-sm">
                When someone shares a group with you, you'll see it here. You'll be able to view and interact with shared media.
            </p>
        </div>
    );
};

interface OwnedGroup {
    id: number;
    name: string;
    created_by: number;
    members: Member[];
    files: Array<{
        media: MediaFile & {
            file_generated_name: string;
            path: string;
        };
        addedBy: {
            id: number;
            name: string;
            email: string;
        };
    }>;
}

interface SharedGroup {
    id: number;
    name: string;
    created_by: {
        name: string;
        email: string;
    };
    is_admin: boolean;
    media: MediaFile[];
    members: Member[];  // Add this
}


interface Member {
    id: number;
    email: string;
    group_id: number;
    user_id?: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

interface GroupCardProps {
    group: OwnedGroup | SharedGroup;
    onEdit: () => void;
    onDelete: () => void;
    onToggle: () => void;
    isExpanded: boolean;
}

const GroupCard: React.FC<GroupCardProps> = ({ group, onEdit, onDelete, onToggle, isExpanded }) => {
    const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);

    // Helper function to determine if group is shared type
    const isSharedGroup = (group: OwnedGroup | SharedGroup): group is SharedGroup => {
        return 'is_admin' in group;
    };

    // Get media items based on group type
    const getMediaItems = () => {
        if (isSharedGroup(group)) {
            return group.media;
        }
        return group.files.map(file => ({
            ...file.media,
            added_by: {
                name: file.addedBy.name,
                id: file.addedBy.id
            }
        }));
    };

    const mediaItems = getMediaItems();

    const canAddMedia = true; // Everyone can add media
    const canEditGroup = !isSharedGroup(group) || (isSharedGroup(group) && group.is_admin);

    return (
        <div className="border rounded-lg shadow-sm">
            <div className="flex items-center justify-between p-4 cursor-pointer" onClick={onToggle}>
                <div className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-blue-500" />
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-medium">{group.name}</h3>
                            {isSharedGroup(group) && !group.is_admin && (
                                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                    Shared by {group.created_by.name}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500">
                            {mediaItems.length} files
                            {!isSharedGroup(group) && ` · ${group.members.length} members`}
                        </p>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-4">
                    {canEditGroup && (
                        <>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <Edit className="w-5 h-5" />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                className="text-red-500 hover:text-red-700"
                            >
                                <Trash className="w-5 h-5" />
                            </button>
                        </>
                    )}
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="p-4 border-t bg-gray-50">
                    <div className="gap-6">
                        {/* Media Grid */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="font-medium">Media Files</h4>
                                {/* {(!isSharedGroup(group) || (isSharedGroup(group) && group.is_admin)) && (
                                    <button
                                        onClick={onEdit}
                                        className="text-blue-500 text-sm hover:text-blue-600"
                                    >
                                        Add Media
                                    </button>
                                )} */}

                                <button
                                    onClick={onEdit}
                                    className="text-blue-500 text-sm hover:text-blue-600 flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Media
                                </button>
                            </div>



                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {mediaItems.map((mediaItem) => (
                                    <div
                                        key={mediaItem.id}
                                        className="relative group cursor-pointer"
                                        onClick={() => setSelectedMedia(mediaItem)}
                                    >
                                        {mediaItem.file_type === 'image' && (
                                            <img
                                                src={mediaItem.temporaryUrl}
                                                alt={mediaItem.file_name}
                                                className="w-full h-24 object-cover rounded-lg"
                                            />
                                        )}
                                        {mediaItem.file_type === 'video' && (
                                            <div className="w-full h-24 relative bg-gray-900 rounded-lg">
                                                <video
                                                    src={mediaItem.temporaryUrl}
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <Video className="w-8 h-8 text-white opacity-75" />
                                                </div>
                                            </div>
                                        )}
                                        {mediaItem.file_type === 'audio' && (
                                            <div className="w-full h-24 flex items-center justify-center bg-gray-100 rounded-lg">
                                                <Music className="w-8 h-8 text-gray-500" />
                                            </div>
                                        )}
                                        {mediaItem.file_type === 'document' && (
                                            <div className="w-full h-24 flex items-center justify-center bg-gray-100 rounded-lg">
                                                <FileText className="w-8 h-8 text-gray-500" />
                                            </div>
                                        )}
                                        <div className="absolute top-2 left-2 text-xs bg-black bg-opacity-50 text-white px-2 py-1 rounded-full opacity-0 group-hover:opacity-100">
                                            Added by: {mediaItem.added_by.name}
                                        </div>

                                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 p-2">
                                            <p className="text-white text-xs truncate">{mediaItem.file_name}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {canEditGroup && (
                            <div className="mt-6">
                                <h4 className="font-medium mb-3">Members</h4>
                                <div className="space-y-2">
                                    {group.members?.map((member) => (
                                        <div
                                            key={member.id}
                                            className="flex items-center justify-between p-2 bg-white rounded-lg"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-4 h-4 text-gray-500" />
                                                <span className="text-sm">{member.email}</span>
                                            </div>
                                            {/* <button
                                                
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <Trash className="w-4 h-4" />
                                            </button> */}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Media Preview Modal */}
            {selectedMedia && (

                <MediaPreviewModal
                    media={selectedMedia}
                    onClose={() => setSelectedMedia(null)}
                />


                // <MediaPreview
                //     media={selectedMedia}
                //     onClose={() => setSelectedMedia(null)}
                // />
            )}
        </div>
    );
};


interface MediaFile {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
}

interface EditGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    group: any;
    onUpdate: () => void;
    currentUserId: number; // Add this to check file ownership
}

// const EditGroupModal: React.FC<EditGroupModalProps> = ({ isOpen, onClose, group, onUpdate }) => {
//     const [name, setName] = useState(group.name);
//     const [newEmail, setNewEmail] = useState('');
//     const [loading, setLoading] = useState(false);
//     const [availableMedia, setAvailableMedia] = useState<MediaFile[]>([]);
//     const [selectedMedia, setSelectedMedia] = useState<MediaFile[]>([]);
//     const [showMediaSelector, setShowMediaSelector] = useState(false);

//     useEffect(() => {
//         setName(group.name);
//         // Initialize selected media with current group media
//         setSelectedMedia(group.files.map(f => f.media));
//         fetchAvailableMedia();
//     }, [group]);

//     const fetchAvailableMedia = async () => {
//         try {
//             const response: any = await DashboardApi.getMedia();
//             if (response.data?.data?.files?.data) {
//                 setAvailableMedia(response.data.data.files.data);
//             }
//         } catch (error) {
//             console.error('Error fetching media:', error);
//         }
//     };

//     const handleAddEmail = async () => {
//         if (!newEmail) return;

//         setLoading(true);
//         try {
//             const response: any = await DashboardApi.updateGroup(group.id, {
//                 add_emails: [newEmail]
//             });

//             if (response.data?.status) {
//                 toast.success('Member added successfully');
//                 setNewEmail('');
//                 onUpdate();
//             }
//         } catch (error) {
//             toast.error('Failed to add member');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleRemoveMember = async (email: string) => {
//         setLoading(true);
//         try {
//             const response: any = await DashboardApi.updateGroup(group.id, {
//                 remove_emails: [email]
//             });

//             if (response.data?.status) {
//                 toast.success('Member removed successfully');
//                 onUpdate();
//             }
//         } catch (error) {
//             toast.error('Failed to remove member');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleUpdateName = async () => {
//         if (name === group.name) return;

//         setLoading(true);
//         try {
//             const response: any = await DashboardApi.updateGroup(group.id, {
//                 name: name
//             });

//             if (response.data?.status) {
//                 toast.success('Group name updated successfully');
//                 onUpdate();
//             }
//         } catch (error) {
//             toast.error('Failed to update group name');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleUpdateMedia = async () => {
//         setLoading(true);
//         try {
//             // Get current media IDs
//             const currentMediaIds = group.files.map(f => f.media.id);
//             // Get selected media IDs
//             const selectedMediaIds = selectedMedia.map(m => m.id);

//             // Calculate which media to add and remove
//             const mediaToAdd = selectedMediaIds.filter(id => !currentMediaIds.includes(id));
//             const mediaToRemove = currentMediaIds.filter(id => !selectedMediaIds.includes(id));

//             if (mediaToAdd.length === 0 && mediaToRemove.length === 0) {
//                 return;
//             }

//             const response: any = await DashboardApi.updateGroup(group.id, {
//                 add_media_ids: mediaToAdd,
//                 remove_media_ids: mediaToRemove
//             });

//             if (response.data?.status) {
//                 toast.success('Media updated successfully');
//                 onUpdate();
//                 setShowMediaSelector(false);
//             }
//         } catch (error) {
//             toast.error('Failed to update media');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const getMediaIcon = (type: string) => {
//         switch (type) {
//             case 'image': return <Image className="w-6 h-6" />;
//             case 'video': return <Video className="w-6 h-6" />;
//             case 'audio': return <Music className="w-6 h-6" />;
//             default: return <FileText className="w-6 h-6" />;
//         }
//     };

//     if (!isOpen) return null;

//     return (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
//                 <div className="flex justify-between items-center mb-4">
//                     <h2 className="text-xl font-semibold">Edit Group</h2>
//                     <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//                         <X className="w-6 h-6" />
//                     </button>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     {/* Left Column - Group Details & Members */}
//                     <div className="space-y-4">
//                         {/* Group Name */}
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700 mb-1">
//                                 Group Name
//                             </label>
//                             <div className="flex gap-2">
//                                 <input
//                                     type="text"
//                                     value={name}
//                                     onChange={(e) => setName(e.target.value)}
//                                     className="flex-1 p-2 border rounded-lg"
//                                 />
//                                 <button
//                                     onClick={handleUpdateName}
//                                     disabled={name === group.name || loading}
//                                     className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
//                                 >
//                                     Update
//                                 </button>
//                             </div>
//                         </div>

//                         {/* Members Management */}
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700 mb-1">
//                                 Add Member
//                             </label>
//                             <div className="flex gap-2 mb-2">
//                                 <input
//                                     type="email"
//                                     value={newEmail}
//                                     onChange={(e) => setNewEmail(e.target.value)}
//                                     placeholder="Enter email address"
//                                     className="flex-1 p-2 border rounded-lg"
//                                 />
//                                 <button
//                                     onClick={handleAddEmail}
//                                     disabled={!newEmail || loading}
//                                     className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
//                                 >
//                                     <Plus className="w-4 h-4" />
//                                 </button>
//                             </div>

//                             <div className="space-y-2 max-h-48 overflow-y-auto">
//                                 {group?.members?.map((member) => (
//                                     <div key={member.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
//                                         <div className="flex items-center gap-2">
//                                             <Mail className="w-4 h-4 text-gray-500" />
//                                             <span className="text-sm">{member?.email}</span>
//                                         </div>
//                                         <button
//                                             onClick={() => handleRemoveMember(member?.email)}
//                                             disabled={loading}
//                                             className="text-red-500 hover:text-red-700 disabled:opacity-50"
//                                         >
//                                             <Trash className="w-4 h-4" />
//                                         </button>
//                                     </div>
//                                 ))}
//                             </div>
//                         </div>
//                     </div>

//                     {/* Right Column - Media Management */}
//                     <div>
//                         <div className="flex justify-between items-center mb-2">
//                             <label className="block text-sm font-medium text-gray-700">
//                                 Media Files
//                             </label>
//                             <button
//                                 onClick={() => setShowMediaSelector(!showMediaSelector)}
//                                 className="text-blue-500 text-sm hover:text-blue-600"
//                             >
//                                 {showMediaSelector ? 'Done' : 'Edit Media'}
//                             </button>
//                         </div>

//                         {showMediaSelector ? (
//                             <div className="border rounded-lg p-4">
//                                 <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
//                                     {availableMedia.map((media) => {
//                                         const isSelected = selectedMedia.some(m => m.id === media.id);
//                                         return (
//                                             <div
//                                                 key={media.id}
//                                                 onClick={() => setSelectedMedia(prev =>
//                                                     isSelected
//                                                         ? prev.filter(m => m.id !== media.id)
//                                                         : [...prev, media]
//                                                 )}
//                                                 className={`relative cursor-pointer rounded-lg overflow-hidden border-2 
//                 ${isSelected ? 'border-blue-500' : 'border-gray-200'}`}
//                                             >
//                                                 {/* Image Preview */}
//                                                 {media.file_type === 'image' && (
//                                                     <img
//                                                         src={media.temporaryUrl}
//                                                         alt={media.file_name}
//                                                         className="w-full h-24 object-cover"
//                                                     />
//                                                 )}

//                                                 {/* Video Preview */}
//                                                 {media.file_type === 'video' && (
//                                                     <div className="w-full h-24 relative bg-gray-900">
//                                                         <video
//                                                             src={media.temporaryUrl}
//                                                             className="w-full h-full object-cover"
//                                                         />
//                                                         <div className="absolute inset-0 flex items-center justify-center">
//                                                             <Video className="w-8 h-8 text-white opacity-75" />
//                                                         </div>
//                                                     </div>
//                                                 )}

//                                                 {/* Audio Preview */}
//                                                 {media.file_type === 'audio' && (
//                                                     <div className="w-full h-24 flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100">
//                                                         <Music className="w-8 h-8 text-purple-500 mb-1" />
//                                                         <div className="w-3/4 h-1 bg-purple-200 rounded-full">
//                                                             <div className="w-1/2 h-full bg-purple-500 rounded-full" />
//                                                         </div>
//                                                     </div>
//                                                 )}

//                                                 {/* Document Preview */}
//                                                 {media.file_type === 'document' && (
//                                                     <div className="w-full h-24 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
//                                                         <FileText className="w-8 h-8 text-blue-500 mb-1" />
//                                                         <div className="flex flex-col items-center">
//                                                             <div className="w-12 h-1 bg-blue-200 rounded-full mb-1" />
//                                                             <div className="w-8 h-1 bg-blue-200 rounded-full" />
//                                                         </div>
//                                                     </div>
//                                                 )}

//                                                 {/* File Info Overlay */}
//                                                 <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2">
//                                                     <p className="text-white text-xs truncate">{media.file_name}</p>
//                                                     <p className="text-gray-300 text-xs">
//                                                         {media.file_type.charAt(0).toUpperCase() + media.file_type.slice(1)}
//                                                     </p>
//                                                 </div>

//                                                 {/* Selection Indicator */}
//                                                 {isSelected && (
//                                                     <div className="absolute top-2 right-2">
//                                                         <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
//                                                             <Check className="w-4 h-4 text-white" />
//                                                         </div>
//                                                     </div>
//                                                 )}
//                                             </div>
//                                         );
//                                     })}
//                                 </div>
//                                 <button
//                                     onClick={handleUpdateMedia}
//                                     disabled={loading}
//                                     className="mt-4 w-full px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
//                                 >
//                                     {loading ? 'Updating...' : 'Update Media'}
//                                 </button>
//                             </div>
//                         ) : (
//                             <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
//                                 {selectedMedia.map((media) => (
//                                     <div key={media.id} className="relative rounded-lg overflow-hidden">
//                                         {media.file_type === 'image' ? (
//                                             <img
//                                                 src={media.temporaryUrl}
//                                                 alt={media.file_name}
//                                                 className="w-full h-24 object-cover"
//                                             />
//                                         ) : (
//                                             <div className="w-full h-24 flex items-center justify-center bg-gray-100">
//                                                 {getMediaIcon(media.file_type)}
//                                             </div>
//                                         )}
//                                         <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
//                                             <p className="text-white text-xs truncate">{media.file_name}</p>
//                                         </div>
//                                     </div>
//                                 ))}
//                             </div>
//                         )}
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };


const EditGroupModal: React.FC<EditGroupModalProps> = ({
    isOpen,
    onClose,
    group,
    onUpdate,
    currentUserId
}) => {


    const isSharedGroup = (group: any): boolean => {
        return 'media' in group;
    };

    const normalizeFiles = () => {
        if ('media' in group) {
            return group.media.map((item: any) => ({
                media: {
                    id: item.id,
                    file_name: item.file_name,
                    file_type: item.file_type,
                    temporaryUrl: item.temporaryUrl
                },
                added_by: item.added_by
            }));
        }
        return group.files;
    };

    const files = normalizeFiles();

    const isAdmin = isSharedGroup(group) ? group.is_admin : true; // Owned groups are always admin


    const normalizeGroupData = (group: any) => {
        if (isSharedGroup(group)) {
            return {
                ...group,
                files: group.media.map((m: any) => ({
                    media: {
                        id: m.id,
                        file_name: m.file_name,
                        file_type: m.file_type,
                        temporaryUrl: m.temporaryUrl,
                        created_at: m.created_at
                    },
                    added_by: m.added_by // Include added_by info
                }))
            };
        }
        return {
            ...group,
            is_admin: true
        };
    };

    const normalizedGroup = normalizeGroupData(group);
    // console?.log(normalizedGroup)
    // console?.log(group)
    const [name, setName] = useState(normalizedGroup.name);
    const [newEmail, setNewEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [availableMedia, setAvailableMedia] = useState<MediaFile[]>([]);
    const [selectedMedia, setSelectedMedia] = useState<MediaFile[]>([]);
    const [showMediaSelector, setShowMediaSelector] = useState(false);

    useEffect(() => {
        setName(normalizedGroup.name);
        // Initialize selected media with current group media
        setSelectedMedia(normalizedGroup.files.map((f: any) => f.media));
        fetchAvailableMedia();
    }, []);

    const fetchAvailableMedia = async () => {
        try {
            const response: any = await DashboardApi.getMedia(1);
            if (response.data?.data?.files?.data) {
                setAvailableMedia(response.data.data.files.data);
                // console?.log(response.data.data.files.data)
            }
        } catch (error) {
            console.error('Error fetching media:', error);
        }
    };

    const handleAddEmail = async () => {
        if (!newEmail) return;

        setLoading(true);
        try {
            const response: any = await DashboardApi.updateGroup(group.id, {
                add_emails: [newEmail]
            });

            if (response.data?.status) {
                toast.success('Member added successfully');
                setNewEmail('');
                onUpdate();
            }
        } catch (error) {
            toast.error('Failed to add member');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = async (email: string) => {
        setLoading(true);
        try {
            const response: any = await DashboardApi.updateGroup(group.id, {
                remove_emails: [email]
            });

            if (response.data?.status) {
                toast.success('Member removed successfully');
                onUpdate();
            }
        } catch (error) {
            toast.error('Failed to remove member');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateName = async () => {
        if (name === group.name) return;

        setLoading(true);
        try {
            const response: any = await DashboardApi.updateGroup(group.id, {
                name: name
            });

            if (response.data?.status) {
                toast.success('Group name updated successfully');
                onUpdate();
            }
        } catch (error) {
            toast.error('Failed to update group name');
        } finally {
            setLoading(false);
        }
    };

    const getMediaIcon = (type: string) => {
        switch (type) {
            case 'image': return <Image className="w-6 h-6" />;
            case 'video': return <Video className="w-6 h-6" />;
            case 'audio': return <Music className="w-6 h-6" />;
            default: return <FileText className="w-6 h-6" />;
        }
    };

    // const isAdmin = group.is_admin;

    const handleRemoveMedia = async (mediaIds: number[]) => {
        setLoading(true);
        try {
            const response: any = await DashboardApi.updateGroup(group.id, {
                remove_media_ids: mediaIds
            });

            if (response.data?.status) {
                toast.success('Media removed successfully');
                onUpdate();
            }
        } catch (error) {
            toast.error('Failed to remove media');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateMedia = async () => {
        setLoading(true);
        try {

            const currentMediaIds = normalizedGroup.files.map((f: any) => f.media.id);
            const selectedMediaIds = selectedMedia.map(m => m.id);



            // If not admin, only allow adding new media
            if (!isAdmin) {
                const mediaToAdd = selectedMediaIds.filter(id => !currentMediaIds.includes(id));


                if (mediaToAdd.length === 0) return;



                const response: any = await DashboardApi.updateGroup(group.id, {
                    add_media_ids: mediaToAdd,
                });

                if (response.data?.status) {
                    toast.success('Media added successfully');
                    onUpdate();
                    setShowMediaSelector(false);
                }
            } else {
                // Admin can both add and remove media
                const mediaToAdd = selectedMediaIds.filter(id => !currentMediaIds.includes(id));
                const mediaToRemove = currentMediaIds.filter((id: any) => !selectedMediaIds.includes(id));

                if (mediaToAdd.length === 0 && mediaToRemove.length === 0) return;

                const response: any = await DashboardApi.updateGroup(group.id, {
                    add_media_ids: mediaToAdd,
                    remove_media_ids: mediaToRemove
                });

                if (response.data?.status) {
                    toast.success('Media updated successfully');
                    onUpdate();
                    setShowMediaSelector(false);
                }
            }
        } catch (error) {
            toast.error('Failed to update media');
            console?.log(error)
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">
                        {isAdmin ? 'Edit Group' : 'Add Media to Group'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column - Only show for admin */}
                    {isAdmin && (
                        <div className="space-y-4">
                            {/* Group Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Group Name
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="flex-1 p-2 border rounded-lg"
                                    />
                                    <button
                                        onClick={handleUpdateName}
                                        disabled={name === group.name || loading}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                                    >
                                        Update
                                    </button>
                                </div>
                            </div>

                            {/* Members Management */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Members
                                </label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        placeholder="Enter email address"
                                        className="flex-1 p-2 border rounded-lg"
                                    />
                                    <button
                                        onClick={handleAddEmail}
                                        disabled={!newEmail || loading}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {group.members.map((member: any) => (
                                        <div key={member.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-4 h-4 text-gray-500" />
                                                <span className="text-sm">{member.email}</span>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveMember(member.email)}
                                                disabled={loading}
                                                className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                            >
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Right Column - Media Management */}
                    <div className={isAdmin ? '' : 'col-span-2'}>
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Media Files
                            </label>
                            <button
                                onClick={() => setShowMediaSelector(!showMediaSelector)}
                                className="text-blue-500 text-sm hover:text-blue-600"
                            >
                                {showMediaSelector ? 'Done' : 'Add Media'}
                            </button>
                        </div>

                        {/* Media Selection Grid */}
                        {showMediaSelector ? (
                            <div className="border rounded-lg p-4">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                                    {availableMedia.map((media) => {
                                        const isSelected = selectedMedia.some(m => m.id === media.id);
                                        // setSelectedMedia(selectedMedia)

                                        return (
                                            <div
                                                key={media.id}
                                                onClick={() => setSelectedMedia(prev =>
                                                    isSelected
                                                        ? prev.filter(m => m.id !== media.id)
                                                        : [...prev, media]
                                                )}
                                                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 
                                                    ${isSelected ? 'border-blue-500' : 'border-gray-200'}`}
                                            >
                                                {/* Image Preview */}
                                                {media.file_type === 'image' && (
                                                    <img
                                                        src={media.temporaryUrl}
                                                        alt={media.file_name}
                                                        className="w-full h-24 object-cover"
                                                    />
                                                )}

                                                {/* Video Preview */}
                                                {media.file_type === 'video' && (
                                                    <div className="w-full h-24 relative bg-gray-900">
                                                        <video
                                                            src={media.temporaryUrl}
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <Video className="w-8 h-8 text-white opacity-75" />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Audio Preview */}
                                                {media.file_type === 'audio' && (
                                                    <div className="w-full h-24 flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100">
                                                        <Music className="w-8 h-8 text-purple-500 mb-1" />
                                                        <div className="w-3/4 h-1 bg-purple-200 rounded-full">
                                                            <div className="w-1/2 h-full bg-purple-500 rounded-full" />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Document Preview */}
                                                {media.file_type === 'document' && (
                                                    <div className="w-full h-24 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                                                        <FileText className="w-8 h-8 text-blue-500 mb-1" />
                                                        <div className="flex flex-col items-center">
                                                            <div className="w-12 h-1 bg-blue-200 rounded-full mb-1" />
                                                            <div className="w-8 h-1 bg-blue-200 rounded-full" />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* File Info Overlay */}
                                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2">
                                                    <p className="text-white text-xs truncate">{media.file_name}</p>
                                                    <p className="text-gray-300 text-xs">
                                                        {media.file_type.charAt(0).toUpperCase() + media.file_type.slice(1)}
                                                    </p>
                                                </div>

                                                {/* {(isAdmin || media.added_by?.id === currentUserId) && ( */}
                                                {isSelected && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const mediaToRemove = [media.id];
                                                            handleRemoveMedia(mediaToRemove);
                                                        }}
                                                        className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"
                                                    >
                                                        <Trash className="w-4 h-4" />
                                                    </button>
                                                )}

                                                {/* Selection Indicator */}
                                                {/* {isSelected && (
                                                    <div className="absolute top-2 right-2">
                                                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                                            <Check className="w-4 h-4 text-white" />
                                                        </div>
                                                    </div>
                                                )} */}

                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={handleUpdateMedia}
                                    disabled={loading}
                                    className="mt-4 w-full px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                                >
                                    {loading ? 'Updating...' : isAdmin ? 'Update Media' : 'Add Selected Media'}
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {files.map((file: any) => (
                                    <div key={file.media.id} className="relative rounded-lg overflow-hidden group">
                                        {file.media.file_type === 'image' ? (
                                            <img
                                                src={file.media.temporaryUrl}
                                                alt={file.media.file_name}
                                                className="w-full h-24 object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-24 flex items-center justify-center bg-gray-100">
                                                {getMediaIcon(file.media.file_type)}
                                            </div>
                                        )}

                                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2">
                                            <p className="text-white text-xs truncate">{file.media.file_name}</p>
                                            <p className="text-gray-300 text-xs">
                                                Added by: {file.added_by?.name || 'Unknown'}
                                            </p>
                                        </div>

                                        {(isAdmin || file.added_by?.id === currentUserId) && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveMedia([file.media.id]);
                                                }}
                                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


interface GroupMember {
    id: number;
    email: string;
}

interface GroupMedia {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
}

interface GroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    media: Media[];
}

// const GroupSharingModal: React.FC<GroupModalProps> = ({ isOpen, onClose, media }) => {
//     const [groupName, setGroupName] = useState('');
//     const [selectedMedia, setSelectedMedia] = useState<GroupMedia[]>([]);
//     const [members, setMembers] = useState<GroupMember[]>([]);
//     const [newEmail, setNewEmail] = useState('');
//     const [searchTerm, setSearchTerm] = useState('');
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState('');

//     const handleAddMember = (e: React.FormEvent) => {
//         e.preventDefault();
//         if (newEmail && !members.find(m => m.email === newEmail)) {
//             setMembers([...members, { id: Date.now(), email: newEmail }]);
//             setNewEmail('');
//         }
//     };

//     const handleRemoveMember = (email: string) => {
//         setMembers(members.filter(m => m.email !== email));
//     };

//     const handleToggleMedia = (mediaItem: GroupMedia) => {
//         const isSelected = selectedMedia.find(m => m.id === mediaItem.id);
//         if (isSelected) {
//             setSelectedMedia(selectedMedia.filter(m => m.id !== mediaItem.id));
//         } else {
//             setSelectedMedia([...selectedMedia, mediaItem]);
//         }
//     };

//     const handleCreateGroup = async () => {
//         if (!groupName) {
//             setError('Group name is required');
//             return;
//         }
//         if (members.length === 0) {
//             setError('Add at least one member');
//             return;
//         }
//         if (selectedMedia.length === 0) {
//             setError('Select at least one media file');
//             return;
//         }

//         setLoading(true);
//         try {
//             const response: any = await DashboardApi.createGroup({
//                 name: groupName,
//                 emails: members.map(m => m.email),
//                 media_ids: selectedMedia.map(m => m.id)
//             });

//             if (response.data?.status) {
//                 toast.success('Group created successfully');
//                 onClose();
//                 window.location.reload()
//             }
//         } catch (error: any) {
//             setError(error.response?.data?.message || 'Failed to create group');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const filteredMedia = media.filter(m =>
//         m.file_name.toLowerCase().includes(searchTerm.toLowerCase())
//     );

//     if (!isOpen) return null;

//     return (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
//                 <div className="flex justify-between items-center mb-6">
//                     <h2 className="text-xl font-semibold">Create Sharing Group</h2>
//                     <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//                         <X className="w-6 h-6" />
//                     </button>
//                 </div>

//                 <div className="space-y-6">
//                     {/* Group Name Input */}
//                     <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                             Group Name
//                         </label>
//                         <input
//                             type="text"
//                             value={groupName}
//                             onChange={(e) => setGroupName(e.target.value)}
//                             className="w-full p-2 border rounded-lg"
//                             placeholder="Enter group name"
//                         />
//                     </div>

//                     {/* Members Section */}
//                     <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                             Add Members
//                         </label>
//                         <form onSubmit={handleAddMember} className="flex gap-2 mb-4">
//                             <input
//                                 type="email"
//                                 value={newEmail}
//                                 onChange={(e) => setNewEmail(e.target.value)}
//                                 className="flex-1 p-2 border rounded-lg"
//                                 placeholder="Enter email address"
//                             />
//                             <button
//                                 type="submit"
//                                 className="bg-blue-500 text-white px-4 py-2 rounded-lg"
//                             >
//                                 <Plus className="w-4 h-4" />
//                             </button>
//                         </form>

//                         <div className="space-y-2">
//                             {members.map((member) => (
//                                 <div key={member.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
//                                     <div className="flex items-center gap-2">
//                                         <Mail className="w-4 h-4 text-gray-500" />
//                                         <span>{member.email}</span>
//                                     </div>
//                                     <button
//                                         onClick={() => handleRemoveMember(member.email)}
//                                         className="text-red-500 hover:text-red-700"
//                                     >
//                                         <Trash className="w-4 h-4" />
//                                     </button>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>

//                     {/* Media Selection Section */}
//                     <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                             Select Media
//                         </label>
//                         <div className="flex gap-2 mb-4">
//                             {/* <Search className="w-5 h-5 text-gray-400" /> */}
//                             <input
//                                 type="text"
//                                 value={searchTerm}
//                                 onChange={(e) => setSearchTerm(e.target.value)}
//                                 className="flex-1 p-2 border rounded-lg"
//                                 placeholder="Search media files"
//                             />
//                         </div>

//                         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//                             {filteredMedia.map((item) => (
//                                 <div
//                                     key={item.id}
//                                     onClick={() => handleToggleMedia(item)}
//                                     className={`relative rounded-lg overflow-hidden cursor-pointer border-2 
//                                         ${selectedMedia.find(m => m.id === item.id)
//                                             ? 'border-blue-500'
//                                             : 'border-transparent'}`}
//                                 >
//                                     {item.file_type === 'image' && (
//                                         <img
//                                             src={item.temporaryUrl}
//                                             alt={item.file_name}
//                                             className="w-full h-32 object-cover"
//                                         />
//                                     )}

//                                     {/* Video Preview */}
//                                     {item.file_type === 'video' && (
//                                         <div className="relative w-full h-32 bg-gray-100">
//                                             <video
//                                                 src={item.temporaryUrl}
//                                                 className="w-full h-full object-cover"
//                                             />
//                                             <div className="absolute inset-0 flex items-center justify-center">
//                                                 <Video className="w-8 h-8 text-gray-500" />
//                                             </div>
//                                         </div>
//                                     )}

//                                     {/* Audio Preview */}
//                                     {item.file_type === 'audio' && (
//                                         <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
//                                             <Music className="w-8 h-8 text-gray-500" />
//                                         </div>
//                                     )}

//                                     {/* Document Preview */}
//                                     {item.file_type === 'document' && (
//                                         <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
//                                             <FileText className="w-8 h-8 text-gray-500" />
//                                         </div>
//                                     )}


//                                     <div className="p-2 bg-gray-50">
//                                         <p className="text-sm truncate">{item.file_name}</p>
//                                     </div>

//                                     {selectedMedia.find(m => m.id === item.id) && (
//                                         <div className="absolute top-2 right-2">
//                                             <div className="bg-blue-500 rounded-full p-1">
//                                                 <Check className="w-4 h-4 text-white" />
//                                             </div>
//                                         </div>
//                                     )}
//                                 </div>
//                             ))}
//                         </div>
//                     </div>

//                     {error && (
//                         <p className="text-red-500 text-sm">{error}</p>
//                     )}

//                     <div className="flex justify-end gap-4">
//                         <button
//                             onClick={onClose}
//                             className="px-4 py-2 text-gray-600"
//                         >
//                             Cancel
//                         </button>
//                         <button
//                             onClick={handleCreateGroup}
//                             disabled={loading}
//                             className="px-6 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
//                         >
//                             {loading ? 'Creating...' : 'Create Group'}
//                         </button>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };


interface MediaPreviewProps {
    media: {
        file_name: string;
        file_type: string;
        temporaryUrl: string;
    } | null;
    onClose: () => void;
}

const MediaPreviewModal: React.FC<MediaPreviewProps> = ({ media, onClose }) => {
    if (!media) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative bg-white rounded-lg max-w-3xl w-full mx-4">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-4">
                    {media.file_type === 'video' && (
                        <div className="aspect-video">
                            <video
                                src={media.temporaryUrl}
                                controls
                                className="w-full h-full rounded-lg"
                            >
                                Your browser does not support video playback.
                            </video>
                        </div>
                    )}

                    {media.file_type === 'image' && (
                        <div className="relative max-h-[80vh] overflow-hidden">
                            <img
                                src={media.temporaryUrl}
                                alt={media.file_name}
                                className="w-full object-contain rounded-lg"
                            />
                        </div>
                    )}

                    {media.file_type === 'audio' && (
                        <div className="p-8">
                            <audio
                                src={media.temporaryUrl}
                                controls
                                className="w-full"
                            >
                                Your browser does not support audio playback.
                            </audio>
                        </div>
                    )}

                    {media.file_type === 'document' && (
                        <div className="h-[600px]">
                            <iframe
                                src={media.temporaryUrl}
                                className="w-full h-full border rounded-lg"
                                title={media.file_name}
                            >
                                Your browser does not support document preview.
                            </iframe>
                        </div>
                    )}

                    <div className="mt-4">
                        <h3 className="font-medium">{media.file_name}</h3>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MediaGroups = () => {
    // const [groups, setGroups] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const userLoginData = useSelector((state: any) => state.data.login.value);
    const [demoModalOpen, setDemoModalOpen] = useState(false);

    const [groups, setGroups] = useState<GroupState>({
        owned: [],
        shared: []
    });

    const [activeTab, setActiveTab] = useState<'owned' | 'shared'>('owned');
    const [expandedGroups, setExpandedGroups] = useState<number[]>([]);
    const [selectedMedia, setSelectedMedia] = useState<GroupMedia | null>(null);
    const [media, setMedia] = useState<Media[]>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [showGroupModal, setShowGroupModal] = useState(false);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<any>(null);

    const handleEditGroup = (group: Group) => {
        setSelectedGroup(group);
        setEditModalOpen(true);
    };

    const handleDeleteGroups = async (groupId: number) => {
        setSelectedGroupId(groupId);
        setDeleteModal(true);
    };
    const currentUserId = userLoginData?.data?.user_data?.id;


    useEffect(() => {
        DashboardApi.getMedia(1).then(
            (response: AxiosResponse<any>) => {
                if (response?.data) {
                    setMedia(response?.data?.data?.files?.data)
                    setLoading(false)
                }
            }
        )

        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        try {
            const [ownedResponse, sharedResponse]: [AxiosResponse<any>, AxiosResponse<any>] = await Promise.all([
                DashboardApi.myGroups(),
                DashboardApi.getMySharedGroups()
            ]);

            if (ownedResponse.data?.status && sharedResponse.data?.status) {
                setGroups({
                    owned: ownedResponse.data.data || [],
                    shared: sharedResponse.data.data || []
                });
            }
        } catch (error) {
            toast.error('Failed to fetch groups');
        } finally {
            setLoading(false);
        }
    };

    const toggleGroup = (groupId: number) => {
        setExpandedGroups(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    };

    const getMediaIcon = (type: string) => {
        switch (type) {
            case 'image': return <Image className="w-6 h-6" />;
            case 'video': return <Video className="w-6 h-6" />;
            case 'audio': return <Music className="w-6 h-6" />;
            default: return <FileText className="w-6 h-6" />;
        }
    };

    const handleDeleteGroup = async () => {
        if (!selectedGroupId) return;

        try {
            const response: any = await DashboardApi.deleteGroup(selectedGroupId);
            if (response.data?.status) {
                toast.success('Group deleted successfully');
                fetchGroups();
            }
        } catch (error) {
            toast.error('Failed to delete group');
        } finally {
            setDeleteModal(false);
            setSelectedGroupId(null);
        }
    };

    // if (loading) {
    //     return <div className="flex justify-center items-center h-64">Loading...</div>;
    // }

    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />
                    <div className="">
                        {/* Header with Watch Demo Button */}
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-2xl font-semibold">Media Groups</h1>


                        </div>

                        <div className="mb-6 border-b flex justify-between">
                            <div className="flex space-x-6">
                                <button
                                    onClick={() => setActiveTab('owned')}
                                    className={`pb-2 font-medium transition-colors ${activeTab === 'owned'
                                        ? 'border-b-2 border-blue-500 text-blue-500'
                                        : 'text-gray-500'
                                        }`}
                                >
                                    My Groups
                                </button>
                                <button
                                    onClick={() => setActiveTab('shared')}
                                    className={`pb-2 font-medium transition-colors ${activeTab === 'shared'
                                        ? 'border-b-2 border-blue-500 text-blue-500'
                                        : 'text-gray-500'
                                        }`}
                                >
                                    Shared With Me
                                </button>
                            </div>

                            {activeTab === 'owned' && (
                                <div className='flex gap-2 mb-5'>
                                    <div className="flex items-center gap-4">
                                        {/* Watch Demo Button with Animations */}
                                        <button
                                            onClick={() => setDemoModalOpen(true)}
                                            className="group relative px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 animate-pulse"
                                            style={{
                                                animation: 'pulse 2s infinite, glow 3s ease-in-out infinite alternate'
                                            }}
                                        >
                                            {/* Animated background */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                            {/* Shimmer effect */}
                                            <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-shimmer"></div>

                                            {/* Button content */}
                                            <div className="relative flex items-center gap-2">
                                                <Play className="w-4 h-4 group-hover:animate-bounce" />
                                                <span className="font-semibold text-sm">Watch Demo</span>
                                            </div>

                                            {/* Floating dots animation */}
                                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => setShowGroupModal(true)}
                                        className="flex items-center  gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg"
                                    >
                                        <Plus className="w-5 h-5" />
                                        Create Group
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* ... (keep all existing tab content unchanged) */}
                        {activeTab === 'owned' && (
                            <>
                                {loading ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                                    </div>
                                ) : groups.owned.length === 0 ? (
                                    <EmptyGroups onCreateGroup={() => setShowGroupModal(true)} />
                                ) : (
                                    <div className="space-y-4">
                                        {groups.owned.map((group: any) => (
                                            <GroupCard
                                                key={group.id}
                                                group={group}
                                                onEdit={() => handleEditGroup(group)}
                                                onDelete={() => handleDeleteGroups(group.id)}
                                                onToggle={() => toggleGroup(group.id)}
                                                isExpanded={expandedGroups.includes(group.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'shared' && (
                            <>
                                {loading ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                                    </div>
                                ) : groups.shared.length === 0 ? (
                                    <EmptySharedGroups />
                                ) : (
                                    <div className="space-y-4">
                                        {groups.shared.map((group: any) => (
                                            <GroupCard
                                                key={group.id}
                                                group={group}
                                                onEdit={() => handleEditGroup(group)}
                                                onDelete={() => handleDeleteGroups(group.id)}
                                                onToggle={() => toggleGroup(group.id)}
                                                isExpanded={expandedGroups.includes(group.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Delete Confirmation Modal */}
                        <Modal open={deleteModal} onClose={() => setDeleteModal(false)} center>
                            <div className="p-6">
                                <h3 className="text-lg font-medium mb-4">Delete Group</h3>
                                <p className="text-gray-600 mb-6">Are you sure you want to delete this group? This action cannot be undone.</p>
                                <div className="flex justify-end gap-4">
                                    <button
                                        onClick={() => setDeleteModal(false)}
                                        className="px-4 py-2 text-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteGroup}
                                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </Modal>

                        {/* Demo Video Modal */}
                        <Modal
                            open={demoModalOpen}
                            onClose={() => setDemoModalOpen(false)}
                            center
                            classNames={{
                                overlay: 'bg-black bg-opacity-75',
                                modal: 'bg-transparent shadow-none max-w-4xl w-full p-0 m-4'
                            }}
                        >
                            <div className="relative bg-black rounded-lg overflow-hidden">
                                {/* Close button */}
                                <button
                                    onClick={() => setDemoModalOpen(false)}
                                    className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>

                                {/* Video container */}
                                <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
                                    <video
                                        src={'https://res.cloudinary.com/dobeex527/video/upload/v1752612250/group-media_lttcvk.mov'} // Replace with your actual demo video URL
                                        className="absolute top-0 left-0 w-full h-full object-contain"
                                        controls
                                        preload="metadata"
                                        poster=""
                                        title="Media Groups Demo Video"
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                </div>

                                {/* Video title */}
                                <div className="bg-black text-white p-4">
                                    <h3 className="text-lg font-semibold">Media Groups Demo</h3>
                                    <p className="text-gray-300 text-sm mt-1">
                                        Learn how to create groups, share media, and collaborate with team members
                                    </p>
                                </div>
                            </div>
                        </Modal>

                        <ToastContainer
                            position="top-right"
                            autoClose={2000}
                            hideProgressBar={true}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                        />
                    </div>
                </main>
            </div>

            <GroupSharingModal
                isOpen={showGroupModal}
                onClose={() => setShowGroupModal(false)}
                media={media}
            />

            {selectedGroup && (
                <EditGroupModal
                    isOpen={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setSelectedGroup(null);
                    }}
                    group={selectedGroup}
                    onUpdate={() => {
                        fetchGroups();
                        setEditModalOpen(false);
                        setSelectedGroup(null);
                    }}
                    currentUserId={currentUserId}
                />
            )}
        </div>
    );
};

export default MediaGroups;